import { useQuery, useUserInfo, useGameConfig, usePromotionClient, usePlaygameConfig, useDayjs, computed } from '#imports'

/**
 * Composable to fetch and check promotion game slugs
 *
 * Fetches game slugs that have active player bonuses from the API.
 * Only fetches when the user is logged in.
 *
 * @returns Object containing promotion game data and utility functions
 *
 * @example
 * const { gameHasSpins, gamesWithSpins } = usePromotionGames()
 * const hasPromotion = gameHasSpins('some-game-slug')
 */
export const usePromotionGames = () => {
    const { isLoggedIn } = useUserInfo()
    const dayjs = useDayjs()

    const { gamePromotions } = useGameConfig()
    const { gameMessages } = usePlaygameConfig()

    const { client } = usePromotionClient()

    const query = useQuery(async () => {
        const { data } = await client.GET('/public/player-bonuses/active-with-game-slugs')

        return data || { spins: [], bingo_tickets: [] }
    }, {
        key: 'game:promotionGames',
        enabled: isLoggedIn,
        server: false,
        lazy: true
    })

    const gamesWithSpins = computed(() => query.data.value?.spins || [])
    const gamesWithBingoTickets = computed(() => query.data.value?.bingo_tickets || [])

    const gameHasSpins = (gameSlug: string) => {
        return gamesWithSpins.value.includes(gameSlug)
    }

    const gameHasTickets = (gameSlug: string) => {
        return gamesWithBingoTickets.value.includes(gameSlug)
    }

    const gameHasPrizeDraw = (gameSlug: string): boolean => {
        if (!gameMessages) return false
        const gameMessage = gameMessages[gameSlug]
        if (!gameMessage || !gameMessage.enabled) return false
        const now = dayjs.tz()
        const afterStart = gameMessage.announcementStartTime
            ? now.diff(dayjs(gameMessage.announcementStartTime)) >= 0
            : true
        const beforeEnd = gameMessage.announcementEndTime
            ? now.diff(dayjs(gameMessage.announcementEndTime)) <= 0
            : true
        return afterStart && beforeEnd
    }

    const gameHasPromotion = (gameSlug: string) => {
        if (!gamePromotions) return false

        const now = dayjs.tz()

        return gamePromotions.some(gamePromotion =>
            gamePromotion.slugs.includes(gameSlug)
            && now.isBetween(gamePromotion.startDate, gamePromotion.endDate)
        )
    }

    return query.enrich({
        gamesWithSpins,
        gamesWithBingoTickets,
        gameHasSpins,
        gameHasTickets,
        gameHasPromotion,
        gameHasPrizeDraw,
    })
}
